// @flow
import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import Breadcrumb from 'components/fragments/Breadcrumb';
import ArticleHeader from 'components/newsletter/ArticleHeader';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import ArticleSocial from 'components/newsletter/ArticleSocial';
import ArticleTags from 'components/newsletter/ArticleTags';
import FooterSocial from 'components/footer/FooterSocial';
import Sidebar from 'components/sidebar/Sidebar';
import { getDateFromString } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';
import type { BlocContent } from 'types/NewsletterBloc';
import type { RouterHistory } from 'react-router-dom';
import { handleTagEvent } from 'utils/tagManagerUtils';
import PageNotFound from 'components/app/PageNotFound';
import { STATUS_UNKNOWN, STATUS_SUCCESS } from 'constants/statusConstants';

export type OwnProps = {
  match: {
    params: {
      slug: string
    }
  },
  location: {
    pathname: string,
    search: string,
  }
};

export type StateProps = {
  article: ArticleType,
  articles: ArticleType[],
  status: Status,
  placeholder: string,
  club: string,
};

export type DispatchProps = {
  fetchArticle: (slug: string) => void,
  fetchArticlePreview: (id: number) => void,
  articleFetched: (article: ArticleType) => void
};

type State = {
  shouldDisplayLoading: boolean
};

type HistoryProps = {
  history: RouterHistory
};

type Props = OwnProps & StateProps & DispatchProps & HistoryProps;

class Article extends PureComponent<Props, State> {
  props: Props;
  _links;
  _embedTweet;
  _embedFacebook;
  _embedInstagram;
  _articleContent;

  componentDidMount() {
    const {
      articles,
      match: { params: { slug } },
      articleFetched,
      location: { search },
      fetchArticlePreview,
      fetchArticle,
      status, article: { redirection }
    } = this.props;

    const cachedArticle = articles.find(article => article.slug === slug);

    if (cachedArticle) {
      articleFetched(cachedArticle);
    }
    if (search && /\?p=/.test(search) ) {
      let id = search;
      id = id.replace('?p=', '');
      fetchArticlePreview(parseInt(id, 10));
    } else {
      fetchArticle(this.props.match.params.slug);
    }

    if (status === STATUS_SUCCESS && redirection) {
      return (
        <Redirect to={ { pathname: redirection }} />
      );
    }

    if (window.location.hash) {
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.substr(1));
        if (element) {
          element.scrollIntoView();
        }
      }, 500);
    }

    return null;
  }

  componentWillUnmount() {
    if (this._links) {
      this._links.forEach( link => {
        link.removeEventListener(
          'click',
          handleTagEvent('module telechargement', 'clic telechargement', window.location.pathname)
        );
      });
    }
    if (this._embedTweet) {
      this._embedTweet.forEach( tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication twitter', window.location.pathname)
        );
      });
    }
    if (this._embedFacebook) {
      this._embedFacebook.forEach( tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication facebook', window.location.pathname)
        );
      });
    }
    if (this._embedInstagram) {
      this._embedInstagram.forEach( tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication instagram', window.location.pathname)
        );
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this._articleContent) {
      const downloads = Array.from(this._articleContent.getElementsByClassName('card--download'));
      downloads.forEach((download) => {
        this._links = Array.from(download.getElementsByTagName('a'));
        this._links.forEach( link => {
          link.addEventListener(
            'click',
            handleTagEvent('module telechargement', 'clic telechargement', window.location.pathname)
          );
        });
      });
    }
    // TODO : change to activate click only on the card
    if (this._articleContent) {
      this._embedTweet = Array.from(this._articleContent.getElementsByClassName('is-provider-twitter'));
      this._embedTweet.forEach((tweet) => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication twitter', window.location.pathname)
        );
      });
    }
    if (this._articleContent) {
      this._embedFacebook = Array.from(this._articleContent.getElementsByClassName('is-provider-facebook'));
      this._embedFacebook.forEach((tweet) => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication facebook', window.location.pathname)
        );
      });
    }
    if (this._articleContent) {
      this._embedInstagram = Array.from(this._articleContent.getElementsByClassName('is-provider-instagram'));
      this._embedInstagram.forEach((tweet) => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication instagram', window.location.pathname)
        );
      });
    }
  }

  clickTagManager = (socialName: string) => {
    const { article: { title } } = this.props;
    return handleTagEvent('partage', title, socialName);
  };

  renderIntroBlocs = (blocs: BlocContent[]): any => {
    if (
      blocs.length > 0 &&
      blocs[0] &&
      blocs[0].tagName &&
      blocs[0].tagName === 'div' &&
      blocs[0].attributes &&
      blocs[0].attributes[0].value === 'article__headline') {
      return (<ContentBloc {...blocs[0]} />);
    }
    return '';
  };

  renderTrueBlocs = (blocs: BlocContent[]): any => {
    if (
      blocs.length > 0 &&
      blocs[0] &&
      blocs[0].tagName &&
      blocs[0].tagName === 'div' &&
      blocs[0].attributes &&
      blocs[0].attributes[0].value === 'article__headline') {
        delete blocs[0];
    }
    return blocs.map((blocFigure, index) => <ContentBloc key={index} {...blocFigure} />);
  };

  render() {
    const {
      status,
      article,
      article: {
        label,
        caption,
        title,
        illustration: illustrationFromProps,
        illustration_video,
        blocs,
        tags,
        sidebar,
        date: dateProps,
        redirection,
      },
      placeholder,
      club,
    } = this.props;

    const illustration =
      (illustrationFromProps.includes('/img/placeholder/placeholder-ffr.jpg')
      && placeholder)
      || illustrationFromProps;

    const date = getDateFromString(dateProps);
    if (status === STATUS_UNKNOWN) {
      return <PageNotFound />;
    }

    if (status === STATUS_SUCCESS && redirection) {
      return (
        <Redirect to={ { pathname: redirection }} />
      );
    }

    if (blocs) {
      return (
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9 mb-3">
                    <Breadcrumb
                      breadcrumbStyle="mt-3"
                      elements={[
                        { name: 'accueil', to: '/' },
                        { name: 'actualites', to: `/actualites` },
                        { name: label.name, to: `/actualites/${label.slug}` },
                        title
                      ]}
                    />
                  </div>
                </div>
                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                    video={illustration_video}
                  />
                  <div className="col col-lg-7on9 col-lg-offset-1on9" ref={(r) => this._articleContent = r}>
                    {this.renderIntroBlocs(blocs)}
                    <ArticleSocial
                      url={window.location.href}
                      title={article ? title : ''}
                      club={club}
                    />
                    {this.renderTrueBlocs(blocs)}
                  </div>
                </article>
                <div className="row">
                  <div className="col col-lg-7on9 col-lg-offset-1on9">
                    {tags.length > 0 &&
                      <ArticleTags tags={tags} />
                    }
                    <FooterSocial
                      url={window.location.href}
                      title={title || ''}
                      titleclass="mt-md-3 mt-lg-5 mb-3"
                      clickTagManager={(socialName) => this.clickTagManager(socialName)}
                    />
                  </div>
                </div>
              </section>
            </div>
            <Sidebar article={true} sidebarContent={sidebar} />
          </div>
        </div>
      );
    }

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow">
            <section>
              <div className="row">
                <div className="col col-lg-8on9 mb-3">
                  <Breadcrumb
                    breadcrumbStyle="mt-3"
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'actualites', to: `/actualites` },
                      { name: label.name, to: `/actualites/${label.slug}` },
                      title
                    ]}
                  />
                </div>
                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                    video={illustration_video}
                  />
                  <div className="col col-lg-7on9 col-lg-offset-1on9">
                    <ArticleSocial />
                  </div>
                </article>
                <div className="col col-lg-7on9 col-lg-offset-1on9">
                  <ArticleTags tags={tags} />
                  <h3 className="mt-md-3 mt-lg-5 mb-3 ft-400">Partager</h3>
                  <FooterSocial clickTagManager={this.clickTagManager} />
                </div>
              </div>
            </section>
          </div>
          <Sidebar article={true} />
        </div>
      </div>
    );
  }
}

export default withHelmet(Article)((Helmet, pageProps) => {
  const {
    article: {
      title,
      meta_description,
      ffr_url_canonical,
      illustration,
      date,
      modified,
    },
    organization,
    logo,
  } = pageProps;

  const imageSources = [...document.getElementsByTagName("img")].map(({ src }) => (`"${src}"`)).join(', ');

  const meta = getMetaBySlug('actualites/article');

  return (
  <Helmet>
    <title>{meta.title(title)}</title>
    <meta name={'description'} content={meta_description} />
    <meta name={'keywords'} content={meta.keywords(title)} />
    <meta property={'og:url'} content={window.location.href} />
    <meta property={'og:title'} content={meta.title(title)} />
    <meta property={'og:description'} content={meta_description} />
    <meta property={'og:image'} content={illustration} />
    <meta property={'og:image:url'} content={illustration} />
    <meta name={'twitter:image'} content={illustration} />
    <link rel={'canonical'} href={ffr_url_canonical || window.location.href} />
      <script type="application/ld+json">
        {
          `[{
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${ffr_url_canonical || window.location.href}"
            },
            "headline": "${title}",
            "image": [${imageSources}],
            "description": "${meta_description}",
            "datePublished": "${date}",
            "dateModified": "${modified}",
            "author": {
              "@type": "Organization",
              "name": "${organization}",
              "logo": {
                "@type": "ImageObject",
                "url": "${logo}"
              }
            },
            "publisher": {
                "@type": "Organization",
                "name": "${organization}",
                "logo": {
                  "@type": "ImageObject",
                  "url": "${logo}"
                }
              }
            }
          ]`
        }
      </script>
  </Helmet>
  );
});
